import { ReactNode, Fragment } from 'react'

/**
 * Parses markdown-style syntax into React elements.
 * Supports:
 * - Links: [text](url)
 * - Bold: **text** (can contain links and newlines inside)
 * - Newlines: \n
 *
 * Example: "**Check [status page](https://status.sui.io) for updates**\nSecond line"
 */
export function parseMarkdownLinks(text: string): ReactNode {
  const elements: ReactNode[] = []
  // Regex to match bold sections: **content** (content can include anything except **)
  const boldRegex = /\*\*([\s\S]*?)\*\*/g

  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = boldRegex.exec(text)) !== null) {
    // Parse text before the bold section
    if (match.index > lastIndex) {
      elements.push(...parseInlineElements(text.slice(lastIndex, match.index), elements.length))
    }

    // Parse content inside bold and wrap in <strong>
    const boldContent = match[1]
    const innerElements = parseInlineElements(boldContent, 0)
    elements.push(<strong key={`bold-${elements.length}`}>{innerElements}</strong>)

    lastIndex = match.index + match[0].length
  }

  // Parse remaining text after the last bold section
  if (lastIndex < text.length) {
    elements.push(...parseInlineElements(text.slice(lastIndex), elements.length))
  }

  return elements
}

/**
 * Parses links and newlines (non-bold inline elements)
 */
function parseInlineElements(text: string, keyOffset: number): ReactNode[] {
  const elements: ReactNode[] = []
  // Regex to match links: [text](url) or newlines: \n
  const tokenRegex = /\[([^\]]+)\]\(([^)]+)\)|\n/g

  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = tokenRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      elements.push(
        <Fragment key={`text-${keyOffset}-${elements.length}`}>{text.slice(lastIndex, match.index)}</Fragment>
      )
    }

    if (match[0] === '\n') {
      // Newline
      elements.push(<br key={`br-${keyOffset}-${elements.length}`} />)
    } else {
      // Link: [text](url)
      elements.push(
        <a key={`link-${keyOffset}-${elements.length}`} href={match[2]} target="_blank" rel="noopener noreferrer">
          {match[1]}
        </a>
      )
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    elements.push(<Fragment key={`text-${keyOffset}-${elements.length}`}>{text.slice(lastIndex)}</Fragment>)
  }

  return elements
}
